import type { Experiment, GoalCode, Mood, PathwayStyle } from "@andrum/schemas";
import { t } from "@/i18n";

export type PersonalWhyContext = {
  goals?: GoalCode[];
  joyPathway?: PathwayStyle | string | null;
  calmPathway?: PathwayStyle | string | null;
  /** Mood 0–4 from check-in */
  mood?: Mood | null;
  energy?: number | null;
  stress?: number | null;
  recommendationReason?: string | null;
};

/**
 * Soft personal intro: why this experiment, tied to goals / last check-in when available.
 */
export function buildPersonalWhy(
  experiment: Pick<Experiment, "title" | "category" | "rationale" | "durationMinutes">,
  ctx: PersonalWhyContext
): {
  headline: string;
  whyBody: string;
  personalLine: string | null;
  minutesLine: string;
} {
  const minutesLine = t("experiment.aboutMinutesLine", {
    minutes: experiment.durationMinutes,
  });

  const personalBits: string[] = [];

  if (ctx.stress != null && ctx.stress >= 4) {
    personalBits.push(t("experiment.personal.highStress"));
  } else if (ctx.energy != null && ctx.energy <= 2) {
    personalBits.push(t("experiment.personal.lowEnergy"));
  } else if (ctx.mood != null && ctx.mood <= 1) {
    personalBits.push(t("experiment.personal.lowMood"));
  } else if (ctx.mood != null && ctx.mood >= 3) {
    personalBits.push(t("experiment.personal.goodMood"));
  }

  const goals = ctx.goals ?? [];
  if (goals.length > 0) {
    const goalLabels = goals
      .slice(0, 2)
      .map((g) => t(`onboarding.goals.${g}`))
      .join(t("experiment.personal.goalJoin"));
    personalBits.push(
      t("experiment.personal.fromGoals", { goals: goalLabels })
    );
  }

  if (goals.includes("joy") && ctx.joyPathway === "solo") {
    personalBits.push(t("experiment.personal.joySolo"));
  } else if (goals.includes("joy") && ctx.joyPathway === "social") {
    personalBits.push(t("experiment.personal.joySocial"));
  }
  if (goals.includes("calm") && ctx.calmPathway === "solo") {
    personalBits.push(t("experiment.personal.calmSolo"));
  } else if (goals.includes("calm") && ctx.calmPathway === "social") {
    personalBits.push(t("experiment.personal.calmSocial"));
  }

  const catKey = `experiment.personal.category.${experiment.category}`;
  const categoryHint = t(catKey);
  if (categoryHint && categoryHint !== catKey) {
    personalBits.push(categoryHint);
  }

  const personalLine =
    personalBits.length > 0
      ? personalBits.slice(0, 2).join(" ")
      : ctx.recommendationReason?.trim() || null;

  return {
    headline: t("experiment.introHeadline"),
    whyBody: experiment.rationale,
    personalLine,
    minutesLine,
  };
}
